/**
 * @description
 */

import { getCharIndex } from '@umu/umu-util';
import {
    ESectionType,
    IQuizSectionUserResult,
    IUserValueExtend,
    IQuizSectionInfo,
    IQuizSectionAnswer,
    SCORE_TYPE,
} from '@umu/umu-types';

// 用户回答的扩展数据，开放式问题的评分及点评
function getUserValueExtendData(sectionResult: IQuizSectionUserResult) {
    const userValueExtend: IUserValueExtend = {};
    userValueExtend.user_answer_analysis = sectionResult.user_answer_analysis || '';
    userValueExtend.user_answer_pic = sectionResult.user_answer_pic || [];
    userValueExtend.user_answer_score = sectionResult.user_answer_score || 0;
    userValueExtend.user_answer_score_type = sectionResult.user_answer_score_type || 0;
    userValueExtend.user_answer_status = sectionResult.user_answer_status;
    return userValueExtend;
}

export enum SCORE_RANK {
    EXCELLENT,
    GOOD,
    FAILURE,
}

/** 合并用户提交数据 */
export function getUserQuizResult(sectionInfo: IQuizSectionInfo, sectionResultList?: IQuizSectionUserResult[]) {
    const ret: Required<Pick<IQuizSectionInfo, 'userValue' | 'extraValue' | 'userValueExtend'>> = {
        userValue: [],
        extraValue: '',
        userValueExtend: {},
    };
    if (!sectionResultList) {
        return ret;
    }

    (sectionResultList || []).map((sectionResult) => {
        const answerId = String(sectionResult.user_answer_id); // 组件内存储的都是字符串，所以这里转换一次
        const content = sectionResult.user_answer_content;
        if (sectionInfo.type === ESectionType.TEXTAREA || sectionInfo.type === ESectionType.INPUT) {
            if (content) {
                ret.userValue.push(content);
            }
            ret.userValueExtend = getUserValueExtendData(sectionResult);
        } else {
            if (answerId) {
                ret.userValue.push(answerId);
            }
            if (content) {
                ret.extraValue = content;
            }
        }
    });
    return ret;
}

/** 合并正确答案数据 */
export function getQuizAnswerResult(sectionInfo: IQuizSectionInfo, sectionResultList?: IQuizSectionAnswer[]) {
    const ret: Required<Pick<IQuizSectionInfo, 'rightAnswer' | 'rightAnswerValue'>> = {
        rightAnswer: [],
        rightAnswerValue: [],
    };
    if (!sectionResultList) {
        return ret;
    }

    (sectionResultList || []).map((sectionResult) => {
        const answerId = String(sectionResult.right_answer_id); // 组件内存储的都是字符串，所以这里转换一次
        const content = sectionResult.right_answer_content;
        if (sectionInfo.type === ESectionType.INPUT) {
            if (content) {
                ret.rightAnswerValue.push(content);
            }
        } else {
            if (answerId) {
                ret.rightAnswer.push(answerId);
            }
            if (content) {
                ret.rightAnswerValue.push(content);
            }
        }
    });
    return ret;
}

/** 取用户回答列表 */
export function getUserAnswer(sectionInfo: IQuizSectionInfo) {
    const userValue = sectionInfo.userValue;
    if (!userValue) {
        return [];
    }

    const optionList = sectionInfo.list;
    const ret = userValue.map((id) => {
        const index = optionList.findIndex((option) => String(option.id) === id);
        return getCharIndex(index);
    });
    return ret;
}

/** 取用正确答案列表 - 客观题 */
export function getRightAnswer(sectionInfo: IQuizSectionInfo) {
    const rightAnswer = sectionInfo.rightAnswer;
    if (!rightAnswer) {
        return [];
    }

    const optionList = sectionInfo.list;
    const ret = rightAnswer.map((id) => {
        const index = optionList.findIndex((option) => String(option.id) === id);
        return getCharIndex(index);
    });
    return ret;
}

/** 取用正确答案列表 - 主观题 */
export function getRightAnswerValue(sectionInfo: IQuizSectionInfo) {
    const rightAnswerValue = sectionInfo.rightAnswerValue;
    if (!rightAnswerValue) {
        return [];
    }
    return rightAnswerValue;
}

export function getInputQuestionUserAnswer(sectionInfo: IQuizSectionInfo) {
    if (!sectionInfo.userValue || sectionInfo.userValue.length === 0) {
        return [];
    }
    return sectionInfo.userValue;
}

// 开放式问题，分数等级
// y = f( 得分/总分 )，y 分段:
// [1 - 0.9]: EXCELLENT
// (0.9 - 0.6]: GOOD
// (0.6 - 0]: FAILURE
export function getTextQuestionScoreRank(sectionInfo: IQuizSectionInfo) {
    const full = parseFloat(sectionInfo.extend.setup.score + '');
    const { userValueExtend } = sectionInfo;
    let mark = userValueExtend ? userValueExtend.user_answer_score || 0 : 0;
    mark = parseFloat(mark + '');
    if (full === 0) {
        return SCORE_RANK.EXCELLENT;
    }
    const percent = mark / full;
    if (percent >= 0.9) {
        return SCORE_RANK.EXCELLENT;
    }
    if (percent >= 0.6) {
        return SCORE_RANK.GOOD;
    }
    return SCORE_RANK.FAILURE;
}

// 填空问题，分数等级
// 满分: EXCELLENT
// 满分和0之间: GOOD
// 0: FAILURE
export function getInputQuestionScoreRank(sectionInfo: IQuizSectionInfo) {
    const full = parseFloat(sectionInfo.extend.setup.score + '');
    const { userValueExtend } = sectionInfo;
    let mark = userValueExtend ? userValueExtend.user_answer_score || 0 : 0;
    mark = parseFloat(mark + '');
    if (mark === full) {
        return SCORE_RANK.EXCELLENT;
    }
    if (mark < full && mark > 0) {
        return SCORE_RANK.GOOD;
    }
    if (full === 0) {
        return SCORE_RANK.EXCELLENT;
    }
    return SCORE_RANK.FAILURE;
}

// 是否老考试
export function isOldQuiz(quesitonMode: number) {
    if (quesitonMode === 1 || quesitonMode === 2) {
        return true;
    }
    return false;
}

// 选项问题是否正确
export function isMergeOptionQuestionRight(sectionInfo: IQuizSectionInfo) {
    let isRight = 0;
    const userValue = sectionInfo.userValue;
    const rightAnswer = sectionInfo.rightAnswer;
    const usePartailScore = sectionInfo.type === ESectionType.CHECKBOX && sectionInfo.score_type === SCORE_TYPE.PARTIAL;

    if (!userValue || !rightAnswer) {
        return isRight;
    }

    if (userValue.length > rightAnswer.length) {
        return isRight;
    }

    if (userValue.length !== rightAnswer.length && !usePartailScore) {
        return isRight;
    }

    if (userValue.length === 0) {
        return isRight;
    }

    for (let i = 0, len = userValue.length; i < len; i++) {
        if (rightAnswer.indexOf(userValue[i]) === -1) {
            return isRight;
        }
    }

    isRight = (userValue.length === rightAnswer.length) ? 1 : 2;

    return isRight;
}
